/**
 * @file `SigninCtaIsland` — the three-flow sign-in capture section.
 * @module @labcraft/www/ui/islands/signin-cta
 *
 * One island backs every capture surface on the site — the landing's
 * closing CTA, the manifesto's close, and the blog's post-footer
 * capture. Pre-rebuild these were two implementations (the SPA's
 * `SignInCTA` + the blog's hand-rolled static mirror); ADR-026
 * collapses them to this single component wrapped around the shared
 * `@labcraft/ui` `SignInCTA`.
 *
 * Static render (and the client's first paint) is the anonymous,
 * public-beta state. After mount
 * it reads `/api/config` (waitlist copy, identical on every surface)
 * and `/api/account/me` (signed-in learners see no capture section —
 * the whole section renders away, matching the old `learner ? null :`
 * gate in the SPA pages).
 *
 * The CTA is identical site-wide: one copy source (`WAITLIST_COPY`)
 * and one self-contained section layout (`signin_cta.module.css`), so
 * the landing, manifesto, and blog surfaces render the same block.
 *
 * @property wrapperId - Optional DOM id (`start` on the landing — the
 *   nav CTA's `/#start` anchor target).
 * @property spaced - Adds a top-margin gap before the section, for
 *   surfaces whose preceding content doesn't already separate the CTA
 *   (manifesto, blog). The landing omits it — its "From the blog"
 *   section supplies the gap. The block itself is identical either way.
 */

import { useEffect, useState, type JSX } from 'react'
import { SignInCTA } from '@labcraft/ui'
import type { EmailSigninRequest } from '@labcraft/account/proxy-routes'
import { Authorize } from '../authorize.ts'
import { WAITLIST_COPY } from '../waitlist_copy.ts'
import { IslandRuntime } from './runtime.ts'
import styles from './signin_cta.module.css'

/** JSON-safe props for {@link SigninCtaIsland} (see file JSDoc). */
export type SigninCtaIslandProps = {
  wrapperId?: string
  spaced?: boolean
}

/**
 * The capture island. See the file JSDoc for the variant/props
 * contract.
 *
 * @param props - See {@link SigninCtaIslandProps}.
 * @returns The capture section, or `null` for signed-in learners.
 */
export function SigninCtaIsland(
  props: SigninCtaIslandProps,
): JSX.Element | null {
  const [signedIn, setSignedIn] = useState(false)
  // Public beta is the cold-load fallback; if waitlist mode is re-enabled,
  // the runtime config swaps the capture copy back.
  const [waitlistEnabled, setWaitlistEnabled] = useState(false)

  useEffect(() => {
    let cancelled = false
    void IslandRuntime.me().then(res => {
      if (!cancelled && res.learner) setSignedIn(true)
    })
    void IslandRuntime.config().then(config => {
      if (!cancelled) setWaitlistEnabled(config.waitlistEnabled)
    })
    return () => {
      cancelled = true
    }
  }, [])

  if (signedIn) return null

  async function submitEmail(args: EmailSigninRequest): Promise<void> {
    await IslandRuntime.www().account.signinWithEmail(
      Authorize.emailSigninRequest(args),
    )
  }

  const copy = WAITLIST_COPY[waitlistEnabled ? 'on' : 'off']

  return (
    <section
      className={
        props.spaced ? `${styles.capture} ${styles.spaced}` : styles.capture
      }
      id={props.wrapperId}>
      <SignInCTA
        title={copy.ctaTitle}
        lede={copy.ctaLede}
        onContinueWithGithub={Authorize.oauthRedirect('github')}
        onContinueWithGoogle={Authorize.oauthRedirect('google')}
        onSubmitEmail={submitEmail}
      />
    </section>
  )
}
